import 'react-pdf/dist/Page/TextLayer.css';
import { Page, Document } from 'react-pdf';
import { useRef, useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';

import { useTranslate } from 'src/locales';
import { streamAuthHeaders } from 'src/services/sse';
import { PresentationErrorCodes, WordPlaceholderChanges } from 'src/services/presentations/presentations.types';

import 'src/components/documents/pdf-worker';

// ----------------------------------------------------------------------

type Props = {
  fileId: string;
  changes: WordPlaceholderChanges;
  /** The document converter isn't switched on here: the editor draws the file in the browser instead. */
  onUnavailable: VoidFunction;
};

/** How long after the last change the page is asked for again. */
export const PAGE_PREVIEW_DELAY_MS = 700;

/**
 * The agency's Word file as it really looks, with the editor's changes drawn in (staging feedback,
 * JT1-1887): the document converter lays it out as Word does, headers, footers, floating images and
 * text boxes included, and the server shades the placeholders and repeats. The text layer is on, so
 * text can be selected on the page. The previous pages stay on screen while new ones arrive. The PDF's
 * bytes are kept, not a `blob:` URL, for the same Content Security Policy reason as the builder's preview.
 */
export default function WordPageView({ fileId, changes, onUnavailable }: Props) {
  const { t } = useTranslate();
  const [file, setFile] = useState<{ data: Uint8Array } | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [pages, setPages] = useState(0);
  const [width, setWidth] = useState(794);
  const box = useRef<HTMLDivElement>(null);
  const shown = useRef(false);

  useEffect(() => {
    const element = box.current;
    if (!element || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.min(900, Math.max(320, Math.floor(entry.contentRect.width)))));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true);
      fetch(`${import.meta.env.VITE_BACKEND_URL}/presentation-templates/word/preview`, {
        method: 'POST',
        signal: controller.signal,
        headers: { ...streamAuthHeaders(), Accept: 'application/pdf', 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_id: fileId, ...changes }),
      })
        .then(async (response) => {
          if (!response.ok) {
            const body = await response.json().catch(() => null);
            if (body?.error_code === PresentationErrorCodes.ConverterUnavailable) {
              onUnavailable();
              return;
            }
            throw new Error(`word preview ${response.status}`);
          }
          const data = new Uint8Array(await response.arrayBuffer());
          shown.current = true;
          setFile({ data });
          setFailed(false);
          setLoading(false);
        })
        .catch((error) => {
          if (error?.name === 'AbortError') return;
          setFailed(true);
          setLoading(false);
        });
    }, shown.current ? PAGE_PREVIEW_DELAY_MS : 0);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // onUnavailable is the editor's; a change to it doesn't need a new page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId, changes]);

  return (
    <Box ref={box}>
      <Box sx={{ height: 3, mb: 1 }}>{loading && file && <LinearProgress />}</Box>
      {failed && <Alert severity="error" sx={{ mb: 2 }}>{t('presentations.word.editor.page_failed')}</Alert>}
      {!file && loading && <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress /></Stack>}
      {file && (
        <Document file={file} onLoadSuccess={({ numPages }) => setPages(numPages)} loading={null}>
          <Stack spacing={2} alignItems="center" sx={{ cursor: 'text' }}>
            {Array.from({ length: pages }, (_, index) => (
              <Box key={index} sx={{ boxShadow: 1, bgcolor: 'common.white' }}>
                <Page pageNumber={index + 1} width={width} renderTextLayer renderAnnotationLayer={false} />
              </Box>
            ))}
          </Stack>
        </Document>
      )}
    </Box>
  );
}
